(function ($, window, document) {
    'use strict';

    /**
     * Configuration object for a user login module.
     *
     * @property {string} baseUrl - The base URL for API endpoints. Defaults to `/user` if not provided by `WayGenAI.apiBase`.
     * @property {string} redirect - The URL to redirect to upon successful login. Defaults to `/user/` if not provided by `WayGenAI.redirect`.
     * @property {Object} i18n - Internationalized messages for user feedback during the login process. Merges custom messages from `WayGenAI.i18n` with the default set of messages.
     * @property {string} i18n.invalidCreds - Message displayed when the user provides invalid credentials.
     * @property {string} i18n.leakedCredentials - Message displayed when Cloudflare identifies a leaked password.
     * @property {string} i18n.tooMany - Message displayed when there are too many login attempts.
     * @property {string} i18n.tryAgainAt - Message displayed to indicate when the user can attempt logging in again (timestamp version).
     * @property {string} i18n.tryAgainIn - Message displayed to indicate when the user can attempt logging in again (relative time version).
     * @property {string} i18n.success - Message displayed after a successful login and prior to redirection.
     * @property {string} i18n.fallback - General message displayed when login fails for any reason not covered by other messages.
     */
    const CFG = {
        baseUrl: window.WayGenAI?.apiBase || '/user',
        redirect: window.WayGenAI?.redirect || '/user/',
        i18n: Object.assign({
            invalidCreds: 'Invalid email or password.',
            leakedCredentials: 'This password has appeared in a known data breach. Reset it before signing in.',
            tooMany: 'Too many login attempts.',
            tryAgainAt: 'Try again at',
            tryAgainIn: 'Try again in',
            success: 'Logged in successfully. Redirecting…',
            fallback: 'Could not log in. Please try again.'
        }, window.WayGenAI?.i18n || {})
    };

    let loginLockoutTimer = null;

    /**
     * Redirects to a safe URL based on the provided input. This method ensures that the URL is on the same origin,
     * uses a valid HTTP/HTTPS protocol, and optionally retains the query parameters.
     *
     * @param {string} url - The target URL to redirect to. This can be either a relative or an absolute URL.
     * @param {Object} [options] - Additional options to customize the redirection behavior.
     * @param {string} [options.fallback='/user/'] - The fallback URL to use if the provided URL is invalid or unsafe.
     * @param {boolean} [options.keepQuery=false] - A flag indicating whether to retain query parameters in the URL.
     * @return {string} The normalized and safe URL to which the redirection can occur.
     */
    function safeRedirect(url, {fallback = '/user/', keepQuery = false} = {}) {
        try {
            const base = window.location.origin;

            // treat url as relative against current origin
            const u = new URL(typeof url === 'string' ? url : '', base);

            // block weird schemes and scheme-relative
            if (u.protocol !== 'https:' && u.protocol !== 'http:') return fallback;

            // only same-origin allowed
            if (u.origin !== base) return fallback;

            // only paths under this site; normalize to path (optionally keep query)
            const path = u.pathname.startsWith('/') ? u.pathname : '/' + u.pathname;
            const normalized = path.endsWith('/') ? path : path + '/';
            return normalized + (keepQuery ? u.search : '');
        } catch {
            return fallback;
        }
    }

    /**
     * Clears the cached user information used by the site header.
     *
     * @return {void} This method does not return a value.
     */
    function clearCachedUser() {
        try {
            localStorage.removeItem('wg_user');
        } catch (_) {
        }
    }

    /**
     * Clears all error messages and resets error-related states in the login interface.
     * This includes hiding the global error message, removing error texts, and resetting
     * the input field validation states.
     *
     * @return {void} No return value.
     */
    function clearErrors() {
        if (loginLockoutTimer) {
            clearTimeout(loginLockoutTimer);
            loginLockoutTimer = null;
        }
        $('#login-global-error').hide().text('');
        $('#login-success').hide().text('');
        $('[data-error-for]').text('').prevAll('.input').removeClass('is-invalid');
    }

    /**
     * Displays an error message for a specific form field or shows a global error message.
     * Updates the UI to indicate the error state of the field.
     *
     * @param {string} field - The identifier of the form field where the error occurred.
     * @param {string} msg - The error message to display for the specified field.
     * @return {void}
     */
    function showFieldError(field, msg) {
        $('#login-success').hide().text('');
        const $err = $(`[data-error-for="${field}"]`);
        if ($err.length) {
            $err.text(msg);
            $(`#${field}`).addClass('is-invalid');
        } else {
            $('#login-global-error').text(msg).show();
        }
    }

    /**
     * Displays a global error message in the designated error message container.
     *
     * @param {string} msg The error message to be displayed.
     * @return {void}
     */
    function showGlobalError(msg) {
        $('#login-success').hide().text('');
        $('#login-global-error').text(msg).show();
    }

    /**
     * Initiates a countdown timer for a login lockout period based on the provided retry time.
     * The function calculates the remaining time until the next retry is allowed and updates a UI component
     * to display the countdown. When the countdown reaches zero, the appropriate lockout message is shown.
     *
     * @param {string} retryAt - A timestamp indicating when the user is allowed to retry login. Must be in a format parsable by `Date.parse`.
     * @return {void} This function does not return a value.
     */
    function startLockoutCountdown(retryAt) {
        if (!retryAt) {
            return;
        }

        const targetMs = Date.parse(retryAt);
        if (Number.isNaN(targetMs)) {
            return;
        }

        if (loginLockoutTimer) {
            clearTimeout(loginLockoutTimer);
            loginLockoutTimer = null;
        }

        const $box = $('#login-global-error').show();
        (function tick() {
            const diff = targetMs - Date.now();
            if (diff <= 0) {
                $box.text(CFG.i18n.tooMany);
                loginLockoutTimer = null;
                return;
            }

            const totalSeconds = Math.floor(diff / 1000);
            const mm = String(Math.floor(totalSeconds / 60)).padStart(2, '0');
            const ss = String(totalSeconds % 60).padStart(2, '0');

            $box.text(`${CFG.i18n.tooMany} ${CFG.i18n.tryAgainIn} ${mm}:${ss}`);
            loginLockoutTimer = setTimeout(tick, 1000);
        })();
    }

    /**
     * Verifies user authentication status when the application loads.
     * Clears any existing authentication token, then sends an AJAX request
     * to check if the user is authenticated. If successful, sets the user
     * information and redirects to a specified location. If the request fails,
     * clears the potentially invalid or revoked token.
     *
     * @return {void} Does not return any value.
     */
    function verifyAuthOnLoad() {
        clearCachedUser();
        $.ajax({
            url: CFG.baseUrl + '/me',
            method: 'GET',
            dataType: 'json'
        })
            .done(function (res) {
                setUser(res.user || res);
                window.location.assign(safeRedirect(CFG.redirect, {fallback: '/user/'}));
            })
            .fail(function () {
                clearCachedUser();
            });
    }

    /**
     * Stores a user object as a JSON string in the local storage under the key 'wg_user'.
     * If an error occurs during the storage process, it is silently caught and ignored.
     *
     * @param {Object} user - The user object to be stored in local storage.
     * @return {void}
     */
    function setUser(user) {
        try {
            localStorage.setItem('wg_user', JSON.stringify(user));
        } catch (_) {
        }
    }

    // run check on page load
    verifyAuthOnLoad();
    $(function () {
        $('#email').trigger('focus');
    });

    // clear field error on input
    $(document).on('input', '#email, #password', function () {
        const id = this.id;
        $(`[data-error-for="${id}"]`).text('');
        $(this).removeClass('is-invalid');
        $('#login-global-error').hide().text('');
    });

    // handle login form submit
    $(document).on('submit', '#wg-login-form', function (e) {
        e.preventDefault();
        clearErrors();
        const $btn = $('#login-submit');
        $btn.prop('disabled', true).find('.spinner').show();

        const payload = {
            email: $('#email').val().trim(),
            password: $('#password').val()
        };

        $.ajax({
            url: CFG.baseUrl + '/login',
            method: 'POST',
            contentType: 'application/json',
            dataType: 'json',
            data: JSON.stringify(payload)
        })
            .done(function (res) {
                setUser(res.user || res);
                clearErrors();
                $('#login-success').text(CFG.i18n.success).show();
                setTimeout(function () {
                    window.location.assign(safeRedirect(CFG.redirect, {fallback: '/user/'}));
                }, 600);
            })
            .fail(function (jqXHR) {
                const status = jqXHR.status;
                const resp = jqXHR.responseJSON;
                if (status === 0) {
                    showGlobalError(CFG.i18n.fallback);
                    return;
                }
                if (status === 401) {
                    showFieldError('password', CFG.i18n.invalidCreds);
                    return;
                }
                if (status === 403 && resp?.detail?.code === 'leaked_credentials') {
                    showGlobalError(CFG.i18n.leakedCredentials);
                    return;
                }
                if (status === 429) {
                    const detail = resp?.detail || {};
                    const msg = detail.message || CFG.i18n.tooMany;
                    const retryAt = detail.retry_at || null;
                    showGlobalError(msg);
                    startLockoutCountdown(retryAt);
                    return;
                }
                if (status === 422 && Array.isArray(resp?.detail)) {
                    resp.detail.forEach(function (err) {
                        const loc = (err.loc || []).slice(-1)[0];
                        const msg = err.msg || 'Invalid value';
                        if (loc === 'email' || loc === 'password') showFieldError(loc, msg);
                        else showGlobalError(msg);
                    });
                    return;
                }
                showGlobalError(resp?.detail || CFG.i18n.fallback);
            })
            .always(function () {
                $btn.prop('disabled', false).find('.spinner').hide();
            });
    });

    // go to reset password page
    $(document).on('click', '#go-forgot', function () {
        window.location.assign('/user/reset-password/');
    });

})(jQuery, window, document);
